import { useEffect, useState } from 'react';
import type { Season, Field } from '../../../../types';
import { getSeasonsByFarmer, getFieldsByFarmer, addSeason, updateSeason } from '../../../../store';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { EmptyState } from '../../../../components/base/EmptyState';
import { showToast } from '../../../../components/base/Toast';
import { formatDate } from '../../../../utils/format';
import { useStore } from '../../../../hooks/useStore';
import { useAuth } from '../../../../hooks/useAuth';
import { useCategories } from '../../../../hooks/useCategories';
import { UNIT_OF_MEASURE_FALLBACK } from '../../../../constants/categoryFallbacks';
import { listScheduleEventsBySeason, getSeasonCompliance } from '../../../../api/schedule';
import type { BackendScheduleEvent, ScheduleCompliance } from '../../../../api/schedule';
import { CropSelect } from '../../../../components/feature/CropSelect';
import { DocumentUploadSlot, type ApplyExtracted } from '../../../../components/feature/DocumentUploadSlot';
import { ClearableInput, ClearableTextarea, FieldError } from '../../../../components/base/ClearableInput';
import { fieldString, seasonCropFromContract } from '../../../../api/aiExtraction';
import { useCrops } from '../../../../hooks/useCrops';
import { matchCatalogName } from '../../../../utils/catalogMatch';
import { isoAfter, isoDate, positive, validate } from '../../../../utils/validators';

const SCHEDULE_STATUS_LABEL: Record<BackendScheduleEvent['status'], string> = {
  PLANNED: 'Kế hoạch',
  DONE: 'Đã thực hiện',
  SKIPPED: 'Bỏ qua',
  OVERDUE: 'Quá hạn',
};

interface SeasonTabProps {
  farmerId: string;
}

const statusOptions: Season['status'][] = ['Chuẩn bị', 'Đang canh tác', 'Sắp thu hoạch', 'Đã thu hoạch', 'Kết thúc', 'Có rủi ro'];

export default function SeasonTab({ farmerId }: SeasonTabProps) {
  useStore();
  const { user } = useAuth();
  const seasons = getSeasonsByFarmer(farmerId);
  const fields = getFieldsByFarmer(farmerId);
  const { names: unitOptions } = useCategories('UNIT_OF_MEASURE', UNIT_OF_MEASURE_FALLBACK);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Season | null>(null);
  const [scheduleEvents, setScheduleEvents] = useState<BackendScheduleEvent[]>([]);
  const [compliance, setCompliance] = useState<ScheduleCompliance | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  useEffect(() => {
    if (!viewing) {
      setScheduleEvents([]);
      setCompliance(null);
      return;
    }
    setScheduleLoading(true);
    Promise.all([listScheduleEventsBySeason(viewing.id), getSeasonCompliance(viewing.id)])
      .then(([events, comp]) => {
        setScheduleEvents(events);
        setCompliance(comp);
      })
      .catch(() => {
        setScheduleEvents([]);
        setCompliance(null);
      })
      .finally(() => setScheduleLoading(false));
  }, [viewing]);
  const [form, setForm] = useState<Partial<Season>>({
    maMuaVu: '',
    name: '',
    fieldId: fields[0]?.id || '',
    cayTrong: '',
    variety: '',
    startDate: '',
    endDate: '',
    expectedYield: 0,
    unit: 'tấn',
    dinhMucVatTu: '',
    status: 'Chuẩn bị',
  });

  const crops = useCrops(user?.htxId);
  // Errors follow the values: an AI-filled value that is wrong shows red at
  // once, an empty required field once "Lưu" was pressed.
  const [submitted, setSubmitted] = useState(false);
  const allErrors = {
    maMuaVu: validate(form.maMuaVu ?? '', [], true, 'mã mùa vụ'),
    name: validate(form.name ?? '', [], true, 'tên mùa vụ'),
    fieldId: form.fieldId ? undefined : 'Vui lòng chọn thửa đất',
    cayTrong: form.cayTrong ? undefined : 'Vui lòng chọn cây trồng',
    variety: validate(form.variety ?? '', [], true, 'giống cây'),
    startDate: validate(form.startDate ?? '', [isoDate], true, 'ngày bắt đầu'),
    endDate: form.endDate ? isoAfter(form.endDate, form.startDate ?? '', 'Ngày kết thúc phải sau ngày bắt đầu') : undefined,
    expectedYield: validate(form.expectedYield ? String(form.expectedYield) : '', [positive('Sản lượng', 100_000_000)]),
  };
  const err = (k: keyof typeof allErrors, value: unknown) => (value || submitted ? allErrors[k] : undefined);

  /** Fills the season from an offtake contract AI read — only what it read,
   * only into inputs still empty unless asked to overwrite. Same reading the
   * HTX's "Tạo mùa vụ" uses. */
  const applyContract: ApplyExtracted = (f, { overwrite }) => {
    setForm((p) => {
      const next = { ...p };
      const { crop: cropName, outputKg: output } = seasonCropFromContract(f, (crops ?? []).map((c) => c.name), matchCatalogName);
      if (cropName && (overwrite || !p.cayTrong)) {
        next.cayTrong = cropName;
        next.cropId = crops?.find((c) => c.name === cropName)?._id;
      }
      const variety = fieldString(f.variety);
      if (variety && (overwrite || !p.variety)) next.variety = variety;
      if (output !== undefined && (overwrite || !p.expectedYield)) {
        next.expectedYield = output;
        next.unit = 'kg';
      }
      return next;
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setSubmitted(false);
    setForm({
      maMuaVu: '',
      name: '',
      fieldId: fields[0]?.id || '',
      cayTrong: '',
      variety: '',
      startDate: '',
      endDate: '',
      expectedYield: 0,
      unit: 'tấn',
      dinhMucVatTu: '',
      status: 'Chuẩn bị',
      });
    setModalOpen(true);
  };

  const openEdit = (season: Season) => {
    setEditingId(season.id);
    setSubmitted(false);
    setForm({ ...season });
    setModalOpen(true);
  };

  const openView = (season: Season) => {
    setViewing(season);
  };

  const closeView = () => setViewing(null);

  const fieldName = (fieldId?: string) => {
    if (!fieldId) return '-';
    const f = fields.find((x) => x.id === fieldId);
    return f ? `${f.name} (${f.maThuaDat})` : '-';
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (Object.values(allErrors).some(Boolean)) {
      showToast('Vui lòng sửa các ô viền đỏ', 'warning');
      return;
    }
    const payload: Omit<Season, 'id'> = {
      farmerId,
      maMuaVu: form.maMuaVu || '',
      name: form.name || '',
      fieldId: form.fieldId || '',
      cayTrong: form.cayTrong || '',
      cropId: form.cropId,
      variety: form.variety || '',
      startDate: form.startDate || '',
      endDate: form.endDate || undefined,
      expectedYield: Number(form.expectedYield) || 0,
      unit: form.unit || 'tấn',
      dinhMucVatTu: form.dinhMucVatTu || undefined,
      status: (form.status as Season['status']) || 'Chuẩn bị',
    };
    try {
      if (editingId) {
        await updateSeason(editingId, payload, farmerId, user?.htxId);
        showToast('Đã cập nhật mùa vụ', 'success');
      } else {
        await addSeason(payload, user?.htxId);
        showToast('Đã tạo mùa vụ mới', 'success');
      }
      setModalOpen(false);
    } catch {
      showToast('Không thể lưu mùa vụ, vui lòng thử lại', 'error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">
          Mùa vụ ({seasons.length})
        </h3>
        <button
          type="button"
          onClick={openCreate}
          className="px-3 py-2 rounded-md text-sm font-medium bg-agrida-700 hover:bg-agrida-800 text-white transition-colors whitespace-nowrap min-h-[44px]"
        >
          <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
            <i className="ri-add-line" />
          </span>
          Tạo mùa vụ
        </button>
      </div>

      {seasons.length === 0 ? (
        <EmptyState icon="ri-calendar-line" title="Chưa có mùa vụ" description="Hãy tạo mùa vụ để theo dõi quá trình canh tác." />
      ) : (
        <div className="space-y-3">
          {seasons.map((season) => (
            <div key={season.id} className="bg-white rounded-xl border border-slate-200 p-4 md:p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900">{season.name}</p>
                    <StatusBadge status={season.status} />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Mã: {season.maMuaVu} · {season.cayTrong} ({season.variety}) · {fieldName(season.fieldId)}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {formatDate(season.startDate)}{season.endDate ? ` - ${formatDate(season.endDate)}` : ''} · Sản lượng dự kiến: {season.expectedYield} {season.unit}
                  </p>
                  {season.dinhMucVatTu && (
                    <p className="text-xs text-slate-500 mt-0.5">Định mức: {season.dinhMucVatTu}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => openView(season)}
                    className="px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
                  >
                    Xem chi tiết
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(season)}
                    className="px-3 py-1.5 rounded-md text-xs font-medium text-agrida-700 bg-agrida-50 hover:bg-agrida-100 transition-colors whitespace-nowrap min-h-[44px]"
                  >
                    Sửa
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View modal */}
      {viewing && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={closeView} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-panel">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-base font-semibold text-slate-900">Chi tiết mùa vụ</h3>
              <button type="button" onClick={closeView} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-400">Mã mùa vụ</p><p className="text-sm font-medium">{viewing.maMuaVu}</p></div>
                <div><p className="text-xs text-slate-400">Tên mùa vụ</p><p className="text-sm font-medium">{viewing.name}</p></div>
                <div><p className="text-xs text-slate-400">Thửa đất</p><p className="text-sm font-medium">{fieldName(viewing.fieldId)}</p></div>
                <div><p className="text-xs text-slate-400">Cây trồng</p><p className="text-sm font-medium">{viewing.cayTrong} ({viewing.variety})</p></div>
                <div><p className="text-xs text-slate-400">Ngày bắt đầu</p><p className="text-sm font-medium">{formatDate(viewing.startDate)}</p></div>
                <div><p className="text-xs text-slate-400">Ngày kết thúc dự kiến</p><p className="text-sm font-medium">{viewing.endDate ? formatDate(viewing.endDate) : '-'}</p></div>
                <div><p className="text-xs text-slate-400">Sản lượng dự kiến</p><p className="text-sm font-medium">{viewing.expectedYield} {viewing.unit}</p></div>
                <div><p className="text-xs text-slate-400">Tình trạng</p><StatusBadge status={viewing.status} /></div>
              </div>
              {viewing.dinhMucVatTu && (
                <div><p className="text-xs text-slate-400">Định mức vật tư dự kiến</p><p className="text-sm">{viewing.dinhMucVatTu}</p></div>
              )}

              <div className="pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700">Lịch canh tác</p>
                  {compliance && compliance.total > 0 && (
                    <span className="text-[10px] text-slate-400">{compliance.done}/{compliance.total} đúng hạn · {compliance.onTimeRate ?? 0}%</span>
                  )}
                </div>
                {scheduleLoading ? (
                  <p className="text-xs text-slate-400">Đang tải...</p>
                ) : scheduleEvents.length === 0 ? (
                  <p className="text-xs text-slate-400">Chưa áp dụng quy trình canh tác cho mùa vụ này.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {scheduleEvents.map((ev) => (
                      <li key={ev._id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs">
                        <span className="min-w-0 truncate text-slate-600">{ev.note ?? ev.eventType} · {formatDate(ev.plannedDate)}</span>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            ev.status === 'DONE'
                              ? 'bg-emerald-100 text-emerald-700'
                              : ev.status === 'OVERDUE'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {SCHEDULE_STATUS_LABEL[ev.status]}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3">
              <button
                type="button"
                onClick={closeView}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-panel">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-base font-semibold text-slate-900">{editingId ? 'Sửa mùa vụ' : 'Tạo mùa vụ'}</h3>
              <button type="button" onClick={() => setModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <DocumentUploadSlot
                label="Hợp đồng bao tiêu (không bắt buộc)"
                hint="Tải ảnh/PDF/Word hợp đồng để AI tự điền cây trồng, giống, sản lượng — tệp chỉ dùng để đọc."
                docType="OFFTAKE_CONTRACT"
                purpose="CONTRACT_DOCUMENT"
                accept="image/*,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onExtracted={applyContract}
              />
              <div>
                <label className="block text-xs text-slate-500 mb-1">Mã mùa vụ *</label>
                <ClearableInput value={form.maMuaVu || ''} onValueChange={(v) => setForm((p) => ({ ...p, maMuaVu: v }))} error={err('maMuaVu', form.maMuaVu)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="mã mùa vụ" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Tên mùa vụ *</label>
                <ClearableInput value={form.name || ''} onValueChange={(v) => setForm((p) => ({ ...p, name: v }))} error={err('name', form.name)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="tên mùa vụ" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Chọn thửa đất *</label>
                <select
                  value={form.fieldId || ''}
                  onChange={(e) => setForm((p) => ({ ...p, fieldId: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                >
                  {fields.map((f) => (
                    <option key={f.id} value={f.id}>{f.name} ({f.maThuaDat})</option>
                  ))}
                </select>
                <FieldError message={submitted ? allErrors.fieldId : undefined} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Cây trồng</label>
                  <CropSelect
                    value={form.cayTrong || ''}
                    htxId={user?.htxId}
                    onChange={(name, crop) => setForm((p) => ({ ...p, cayTrong: name, cropId: crop?._id }))}
                    invalid={submitted && Boolean(allErrors.cayTrong)}
                    className={`w-full px-3 py-2 rounded-xl border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-agrida-500 ${submitted && allErrors.cayTrong ? 'border-red-400' : 'border-slate-200'}`}
                  />
                  <FieldError message={submitted ? allErrors.cayTrong : undefined} />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Giống cây *</label>
                  <ClearableInput value={form.variety || ''} onValueChange={(v) => setForm((p) => ({ ...p, variety: v }))} error={err('variety', form.variety)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="giống cây" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Ngày bắt đầu *</label>
                  <ClearableInput type="date" value={form.startDate || ''} onValueChange={(v) => setForm((p) => ({ ...p, startDate: v }))} error={err('startDate', form.startDate)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="ngày bắt đầu" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Ngày kết thúc dự kiến</label>
                  <ClearableInput type="date" value={form.endDate || ''} onValueChange={(v) => setForm((p) => ({ ...p, endDate: v || undefined }))} error={err('endDate', form.endDate)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="ngày kết thúc" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Sản lượng dự kiến</label>
                  <ClearableInput type="number" step="0.1" value={form.expectedYield ? String(form.expectedYield) : ''} onValueChange={(v) => setForm((p) => ({ ...p, expectedYield: v === '' ? 0 : Number(v) }))} error={err('expectedYield', form.expectedYield)} inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" aria-label="sản lượng" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Đơn vị</label>
                  <select
                    value={form.unit || 'tấn'}
                    onChange={(e) => setForm((p) => ({ ...p, unit: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                  >
                    {unitOptions.length > 0 ? (
                      unitOptions.map((u) => (<option key={u} value={u}>{u}</option>))
                    ) : (
                      <>
                        <option value="tấn">tấn</option>
                        <option value="kg">kg</option>
                        <option value="tạ">tạ</option>
                      </>
                    )}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Định mức vật tư dự kiến</label>
                <ClearableTextarea value={form.dinhMucVatTu || ''} onValueChange={(v) => setForm((p) => ({ ...p, dinhMucVatTu: v }))} rows={2} maxLength={500} placeholder="Ví dụ: Giống 300kg, NPK 500kg..." inputClassName="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Tình trạng mùa vụ</label>
                <select
                  value={form.status || 'Chuẩn bị'}
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as Season['status'] }))}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                >
                  {statusOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2.5 rounded-xl text-sm font-medium bg-agrida-700 hover:bg-agrida-800 text-white transition-colors whitespace-nowrap min-h-[44px]"
              >
                {editingId ? 'Lưu thay đổi' : 'Tạo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
